/**
 * On-device LLM via @mediapipe/tasks-genai + WebGPU.
 *
 * The native LlmInference handle wraps a multi-GB model loaded into GPU
 * memory; we lazy-initialize it on the first call to `complete()` and reuse
 * it for the lifetime of the page (or until `dispose()` is called). Model
 * bytes stream straight from OPFS so we never materialize the full file as
 * an ArrayBuffer.
 */

import { FilesetResolver, LlmInference } from "@mediapipe/tasks-genai";
import { openModelStream, probeModelMagic } from "../data/modelStore";
import { DebugLog } from "../debug/log";
import type { LlmEngine, LlmRequest, LlmResult } from "./types";

const TAG = "engine.local";
const DISPLAY_NAME = "On-device · Gemma";
export const MEDIAPIPE_VERSION = "0.10.36-rc.20260507";
const WASM_BASE_URL = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-genai@${MEDIAPIPE_VERSION}/wasm`;

let llmPromise: Promise<LlmInference> | null = null;
let loadedFromFile: string | null = null;

export interface LocalEngineHandle extends LlmEngine {
  dispose(): Promise<void>;
}

export function isWebGpuAvailable(): boolean {
  return typeof navigator !== "undefined" && Boolean((navigator as { gpu?: unknown }).gpu);
}

export async function describeWebGpu(): Promise<string> {
  if (!isWebGpuAvailable()) return "WebGPU: not available";
  try {
    const gpu = (navigator as unknown as { gpu: { requestAdapter(): Promise<unknown> } }).gpu;
    const adapter = (await gpu.requestAdapter()) as
      | { info?: Record<string, string>; features?: Set<string> }
      | null;
    if (!adapter) return "WebGPU: requestAdapter() returned null";
    const info = adapter.info ?? {};
    const features = adapter.features ? Array.from(adapter.features) : [];
    return [
      `vendor=${info.vendor ?? "?"}`,
      `architecture=${info.architecture ?? "?"}`,
      `device=${info.device ?? "?"}`,
      `description=${info.description ?? "?"}`,
      `features=${features.length}`,
    ].join(" ");
  } catch (e) {
    return `WebGPU adapter error: ${e instanceof Error ? e.message : String(e)}`;
  }
}

export function createLocalEngine(fileName: string): LocalEngineHandle {
  return {
    displayName: DISPLAY_NAME,

    async complete(request: LlmRequest): Promise<LlmResult> {
      DebugLog.i(TAG, `complete() called`, `fileName=${fileName} messages=${request.messages.length}`);
      if (!isWebGpuAvailable()) {
        DebugLog.w(TAG, "WebGPU not available, refusing to call MediaPipe");
        return {
          kind: "error",
          message:
            "WebGPU isn't available in this browser. Use a recent Chrome, Edge, or Safari 18+.",
          source: DISPLAY_NAME,
        };
      }
      try {
        const llm = await ensureEngine(fileName);
        const prompt = formatPrompt(request);
        DebugLog.d(TAG, `formatted prompt`, `${prompt.length} chars; first 120: ${prompt.slice(0, 120)}`);
        DebugLog.i(TAG, "calling generateResponse...");
        const t0 = performance.now();
        const text = (await llm.generateResponse(prompt)).trim();
        const ms = Math.round(performance.now() - t0);
        DebugLog.i(TAG, `generateResponse returned ${text.length} chars in ${ms}ms`);
        if (text.length === 0) {
          return {
            kind: "error",
            message: "Empty response from on-device model.",
            source: DISPLAY_NAME,
          };
        }
        return { kind: "success", text, source: DISPLAY_NAME };
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        DebugLog.e(TAG, `inference threw: ${message}`, e);
        return { kind: "error", message, source: DISPLAY_NAME };
      }
    },

    async dispose(): Promise<void> {
      const existing = llmPromise;
      llmPromise = null;
      loadedFromFile = null;
      if (existing) {
        try {
          const llm = await existing;
          llm.close();
          DebugLog.d(TAG, "disposed LlmInference handle");
        } catch (e) {
          DebugLog.w(TAG, "dispose threw", e instanceof Error ? e.message : String(e));
        }
      }
    },
  };
}

async function ensureEngine(fileName: string): Promise<LlmInference> {
  if (llmPromise && loadedFromFile === fileName) {
    DebugLog.d(TAG, "reusing cached LlmInference handle");
    return llmPromise;
  }
  if (llmPromise && loadedFromFile !== fileName) {
    DebugLog.i(TAG, `model file changed (${loadedFromFile} -> ${fileName}), disposing old handle`);
    try {
      const previous = await llmPromise;
      previous.close();
    } catch {
      /* ignore */
    }
    llmPromise = null;
    loadedFromFile = null;
  }

  loadedFromFile = fileName;
  llmPromise = (async () => {
    DebugLog.i(TAG, `loading model from OPFS: ${fileName}`);
    const probe = await probeModelMagic(fileName);
    DebugLog.i(
      TAG,
      `model file probe: size=${probe.sizeBytes ?? "?"} magicHex=${probe.magicHex ?? "?"} magicAscii=${probe.magicAscii ?? "?"}`,
    );

    const stream = await openModelStream(fileName);
    if (!stream) {
      throw new Error(
        "Model file not found in OPFS. Open Settings → On-device model and install it first.",
      );
    }

    DebugLog.i(TAG, `loading mediapipe wasm fileset`, `version=${MEDIAPIPE_VERSION} from=${WASM_BASE_URL}`);
    const t0 = performance.now();
    const wasmFileset = await FilesetResolver.forGenAiTasks(WASM_BASE_URL);
    DebugLog.d(TAG, `fileset ready (${Math.round(performance.now() - t0)}ms)`);

    DebugLog.i(TAG, "calling LlmInference.createFromModelBuffer (cold start can take 5-15s)...");
    const t1 = performance.now();
    const llm = await LlmInference.createFromModelBuffer(wasmFileset, stream.getReader());
    DebugLog.i(TAG, `LlmInference loaded successfully (${Math.round(performance.now() - t1)}ms)`);
    return llm;
  })();

  try {
    return await llmPromise;
  } catch (e) {
    DebugLog.e(TAG, "ensureEngine failed", e);
    llmPromise = null;
    loadedFromFile = null;
    throw e;
  }
}

function formatPrompt(request: LlmRequest): string {
  // Gemma's expected chat format: each turn wrapped with start_of_turn /
  // end_of_turn, then an open <start_of_turn>model tag to coax the reply.
  let out = "";
  for (const msg of request.messages) {
    const role = msg.role === "assistant" ? "model" : "user";
    out += `<start_of_turn>${role}\n${msg.content}<end_of_turn>\n`;
  }
  out += "<start_of_turn>model\n";
  return out;
}
